import React, { useEffect, useMemo } from 'react';
import { transform, useAuth0 } from 'utils';
import { usePolicy } from 'utils/usePolicy';
import formats from 'utils/formats';
import { isH3TXPolicy, isSSICPolicy } from 'utils/policyType';

import { Typography, ExpansionPanel, Link, Spinner } from '../../alf-design';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

const SLIDE_REMIT_ADDRESS = {
  companyName: 'Slide Insurance Company',
  poBox: 'PO BOX: 632912',
  cityStateZip: 'Cincinnati, OH 45263',
  overnight: [
    'Fifth Third Bank',
    'c/o LBX Slide Insurance Company 632912',
    '5050 Kingsley Drive MD - 1MOC1Q',
    'Cincinnati, OH 45227',
  ],
};

const SLIDE_SPECIALTY_REMIT_ADDRESS = {
  companyName: 'Slide Specialty Insurance Company',
  poBox: 'PO BOX: 632914',
  cityStateZip: 'Cincinnati, OH 45263',
  overnight: [
    'Fifth Third Bank',
    'c/o LBX Slide Specialty Insurance Company 632914',
    '5050 Kingsley Drive MD - 1MOC1Q',
    'Cincinnati, OH 45227',
  ],
};

const SLIDE_MGA_REMIT_ADDRESS = {
  companyName: 'Slide MGA LLC',
  poBox: 'PO Box 632869',
  cityStateZip: 'Cincinnati, OH 45263',
  overnight: [
    'Fifth Third Bank',
    'c/o LBX Slide MGA LLC 632869',
    '5050 Kingsley Drive MD 1MOC1Q',
    'Cincinnati, OH 45227',
  ],
};

const Help = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { policies, selectedPolicy, selectedPolicyFusionNH, loading, error } =
    usePolicy({
      loadPolicies: true,
    });
  const { user } = useAuth0();
  const isAdmin = user
    ? user?.email?.includes('@slideinsurance.com') && user?.role?.isAdmin
    : false;

  const policyFromUrl = searchParams.get('policy');

  const policy = useMemo(() => {
    if (isAdmin || !policies?.length) return {};
    const match =
      (policyFromUrl &&
        policies.find((p) => p.policyNumber === policyFromUrl)) ||
      policies[0];
    return selectedPolicy || transform.policy(match);
  }, [isAdmin, policies, policyFromUrl]);

  useEffect(() => {
    if (isAdmin || !policies?.length || policyFromUrl) return;

    navigate(`/help?policy=${encodeURIComponent(policies[0].policyNumber)}`, {
      replace: true,
    });
  }, [isAdmin, policies, policyFromUrl, navigate]);

  useEffect(() => {
    if (error && error.entity !== 'claims') {
      navigate('/', { replace: true });
    }
  }, [error]);

  useEffect(() => {
    document.title = 'FAQs | Slide Insurance';
  }, [location.search]);

  const agencyData = (showAddress, noMargin = false) => {
    if (policy?.agent) {
      const agency = {
        name: policy?.agent?.organizationName,
        phoneNumber: formats.phoneFormat(policy?.agent?.phone),
        address: `${policy?.agent?.street}, ${policy?.agent?.city}, ${
          policy?.agent?.state
        } ${formats.zipFormat(policy?.agent?.zip)}`,
        email: policy?.agent?.email,
      };

      return (
        <div
          className={`flex flex-col gap-[8px] ${noMargin ? 'mt-0' : '!mt-3'}`}
        >
          <Typography type="semibold" component="span">
            {agency.name}
          </Typography>

          {showAddress && (
            <Typography
              type="semibold"
              component="span"
              data-testid="agencyAddress"
            >
              {agency.address}
            </Typography>
          )}

          {agency.email && (
            <Link
              id="agencyEmail"
              type="primary"
              href={`mailto:${agency.email}`}
              email
            >
              {agency.email}
            </Link>
          )}

          {agency.phoneNumber && (
            <Link
              id="agencyPhone"
              type="primary"
              href={`tel:${agency.phoneNumber}`}
            >
              {agency.phoneNumber}
            </Link>
          )}
        </div>
      );
    }

    return null;
  };

  const customerCareNumber = '(800) 748-2030';
  const isSlideMga = policy?.isFusion || isH3TXPolicy(policy?.policyNumber);
  const isSSICCompany = isSSICPolicy(
    policy?.policyNumber,
    policy?.policySystem,
  );
  const mailingAddress = isSlideMga
    ? SLIDE_MGA_REMIT_ADDRESS
    : isSSICCompany
      ? SLIDE_SPECIALTY_REMIT_ADDRESS
      : SLIDE_REMIT_ADDRESS;

  const hurricanePolicyNumber = (() => {
    const policyNumber = policy?.policyNumber;
    if (!policyNumber) return policyNumber;
    if (policy?.isFusion && policyNumber.endsWith('N')) {
      return policyNumber.slice(0, -1);
    }
    return policyNumber;
  })();

  const mailingMemoNumberLabel = isSlideMga
    ? 'account number'
    : 'policy number';

  const claimPolicyNumberAnswer = policy?.isFusion ? (
    <>
      <Typography id="policyNumber" type="caption1">
        Your policy number(s)-
      </Typography>
      <Typography type="caption1">
        For hurricane losses: {policy?.policyNumber}
      </Typography>
      <Typography type="caption1">
        For non-hurricane losses: {selectedPolicyFusionNH?.policyNumber}
      </Typography>
    </>
  ) : (
    <Typography id="policyNumber" type="caption1">
      Your policy number: {policy?.policyNumber}.
    </Typography>
  );

  const customerCare = (
    <div className="flex flex-col gap-[8px] mt-[8px]">
      <Typography type="semibold">Customer Service</Typography>

      <Link
        id="customerCareNumber"
        type="primary"
        href={`tel:${customerCareNumber}`}
      >
        {customerCareNumber}
      </Link>
    </div>
  );

  let dataPolicies = [
    {
      isCategory: true,
      title: 'Policy',
    },
    {
      datadogActionName: 'help.faq.policy.get_policy_assistance.open',
      datadogCloseActionName: 'help.faq.policy.get_policy_assistance.close',
      question: 'Who do I contact for assistance with my policy?',
      answer: (
        <div id="help-collapse-text0">
          <Typography component="span" type="caption1">
            You can contact your insurance agent:
            <div className="mt-[8px]">{agencyData()}</div>
          </Typography>
          <br />
          <Typography component="span" type="caption1">
            You can also contact Slide Customer Service:
            {customerCare}
          </Typography>
        </div>
      ),
    },
    {
      datadogActionName: 'help.faq.policy.change_contact_information.open',
      datadogCloseActionName:
        'help.faq.policy.change_contact_information.close',
      question: 'How do I change my contact information?',
      answer: (
        <Typography id="help-collapse-text1" component="span" type="caption1">
          You can update your contact information on your policy by contacting
          your agency:
          {agencyData()}
        </Typography>
      ),
    },
    {
      datadogActionName:
        'help.faq.policy.change_coverages_and_deductibles.open',
      datadogCloseActionName:
        'help.faq.policy.change_coverages_and_deductibles.close',
      question:
        'How do I change the coverages and/or deductibles on my policy?',
      answer: (
        <Typography id="help-collapse-text2" component="span" type="caption1">
          To make coverage changes, please contact your insurance agency:
          {agencyData()}
        </Typography>
      ),
    },
    {
      datadogActionName: 'help.faq.policy.find_insurance_agency.open',
      datadogCloseActionName: 'help.faq.policy.find_insurance_agency.close',
      question: 'Who is my insurance agency?',
      answer: (
        <Typography id="help-collapse-text3" component="span" type="caption1">
          {agencyData(true, true)}
        </Typography>
      ),
    },
    {
      datadogActionName: 'help.faq.policy.get_online_account_assistance.open',
      datadogCloseActionName:
        'help.faq.policy.get_online_account_assistance.close',
      question: 'Who do I contact for assistance with my online account?',
      answer: (
        <div id="help-collapse-text4">
          <Typography component="span" type="caption1">
            {`If you need help accessing your account. Please call customer service at `}
          </Typography>
          <Link
            id="customerCareNumber"
            type="primary"
            href={`tel:${customerCareNumber}`}
            target="_blank"
          >
            {customerCareNumber}
          </Link>
        </div>
      ),
    },
  ];

  const dataClaims = [
    {
      isCategory: true,
      title: 'Claims',
    },
    {
      datadogActionName: 'help.faq.claims.report_accident_or_loss.open',
      datadogCloseActionName: 'help.faq.claims.report_accident_or_loss.close',
      question: 'How should I notify Slide after an accident or loss?',
      answer: (
        <div id="help-collapse-text7">
          <Typography component="span" type="caption1">
            {`Immediately notify us by calling the Slide Claims team: `}
          </Typography>
          <Link type="primary" href="tel:(866) 230-3758">
            (866) 230-3758
          </Link>
        </div>
      ),
    },
    {
      datadogActionName: 'help.faq.claims.file_claim_requirements.open',
      datadogCloseActionName: 'help.faq.claims.file_claim_requirements.close',
      question: 'What information do I need to file a claim?',
      answer: (
        <div id="help-collapse-text8">
          <Typography type="caption1">
            When contacting Slide Insurance about a claim, be ready to provide:
          </Typography>
          <ul>
            <li>
              <Typography type="caption1">
                Your name, address, and phone number.
              </Typography>
            </li>
            <li>
              <Typography type="caption1">
                Name and addresses of witnesses or injured persons (if
                applicable).
              </Typography>
            </li>
            <li>
              <Typography type="caption1">
                General loss information including date and time of loss, type
                of loss, etc.
              </Typography>
            </li>
            <li>{claimPolicyNumberAnswer}</li>
          </ul>
        </div>
      ),
    },
    {
      datadogActionName: 'help.faq.claims.after_reporting_claim.open',
      datadogCloseActionName: 'help.faq.claims.after_reporting_claim.close',
      question: 'What happens after I report a claim?',
      answer: (
        <ul className="mt-0" id="help-collapse-text9">
          <li>
            <Typography type="caption1">
              A Slide claims professional will review your report and start the
              claims process.
            </Typography>
          </li>
          <li>
            <Typography type="caption1">
              We will assign a claim number for tracking purposes.
            </Typography>
          </li>
          <li>
            <Typography type="caption1">
              We will assign the claim to an adjuster who will contact you and
              answer any questions you may have.
            </Typography>
          </li>
          <li>
            <Typography type="caption1">
              Depending on the type of claim and your needs, we may offer
              emergency services from one of our approved vendors.
            </Typography>
          </li>
        </ul>
      ),
    },
  ];

  const dataPayments = [
    {
      isCategory: true,
      title: 'Payments',
    },
    {
      datadogActionName: 'help.faq.payments.accepted_payment_types.open',
      datadogCloseActionName: 'help.faq.payments.accepted_payment_types.close',
      question: 'What types of payment does Slide accept?',
      answer: (
        <div id="help-collapse-text13">
          <Typography type="caption1">
            Payments can be made via e-Check, check, or credit card.
          </Typography>
        </div>
      ),
    },
    {
      datadogActionName: 'help.faq.payments.mailing_address.open',
      datadogCloseActionName: 'help.faq.payments.mailing_address.close',
      question: 'What is my mailing address for payments?',
      answer: (
        <div
          id="help-collapse-text-mailing-address"
          data-testid="help-collapse-text-mailing-address"
        >
          <Typography type="caption1">
            Mail your payment (check or money order) to:
          </Typography>
          <Typography type="caption1">{mailingAddress.companyName}</Typography>
          <Typography type="caption1">{mailingAddress.poBox}</Typography>
          <Typography type="caption1">{mailingAddress.cityStateZip}</Typography>
          <br />
          <Typography type="caption1">Overnight payment:</Typography>
          {mailingAddress.overnight.map((line) => (
            <Typography key={line} type="caption1">
              {line}
            </Typography>
          ))}
          <br />
          <Typography type="caption1" className="font-bold">
            ***Please make checks payable to{' '}
            <span className="text-legacy-primary font-bold">
              {mailingAddress.companyName}
            </span>{' '}
            and include your {mailingMemoNumberLabel}
            <span className="text-legacy-primary font-bold">
              {hurricanePolicyNumber ? `, ${hurricanePolicyNumber},` : ''}
            </span>{' '}
            on the memo line.***
          </Typography>
        </div>
      ),
    },
    {
      datadogActionName: 'help.faq.payments.auto_pay_availability.open',
      datadogCloseActionName: 'help.faq.payments.auto_pay_availability.close',
      question: 'Does Slide have Auto Pay?',
      answer: (
        <div id="help-collapse-text14">
          <Typography type="caption1">
            Slide does not currently offer Auto Pay at this time.
          </Typography>
        </div>
      ),
    },
    {
      datadogActionName: 'help.faq.payments.payment_plan_options.open',
      datadogCloseActionName: 'help.faq.payments.payment_plan_options.close',
      question: 'What payment plan options are available?',
      answer: (
        <div id="help-collapse-text15">
          <Typography component="span" type="caption1">
            Slide offers homeowners Full Pay, 2 Pay, and 4 Pay plan options. To
            make changes to your payment plan option, please contact your agent.
          </Typography>
        </div>
      ),
    },
    {
      datadogActionName: 'help.faq.payments.late_payment_handling.open',
      datadogCloseActionName: 'help.faq.payments.late_payment_handling.close',
      question: 'How does Slide handle late payments?',
      answer: (
        <div id="help-collapse-text16">
          <Typography type="caption1">
            Grace periods and guidelines can vary based on your state and
            payment plan. Please contact your agent or Slide customer service
            for assistance.
          </Typography>
          <br />
          <Typography component="span" type="caption1">
            You can contact your insurance agent:
            {agencyData()}
          </Typography>
          <br />
          <Typography component="span" type="caption1">
            You can also contact Slide Customer Service:
            {customerCare}
          </Typography>
        </div>
      ),
    },
  ];

  if (loading || !policies) {
    return (
      <div className="flex justify-center">
        <Spinner size={80} />
      </div>
    );
  }

  const renderList = (data, title) => {
    return (
      <div className="bg-white mt-[30px] rounded-[10px] pt-[18px] border border-legacy-borderLight">
        <Typography
          withTab
          id={`help-collapse-panel${title}`}
          type="h2"
          component="h2"
          className="!no-underline ml-[15px] mb-[15px] pl-6"
        >
          {title}
        </Typography>

        <ul className="m-0 list-none p-0">
          {data.map(
            (faq, index) =>
              !faq.isCategory && (
                <li key={index} className="px-3">
                  <hr />
                  <ExpansionPanel
                    key={index}
                    type="faq"
                    id={`collapse-button-${index}`}
                    dataDdActionName={faq.datadogActionName}
                    dataDdCloseActionName={faq.datadogCloseActionName}
                    header={
                      <Typography
                        id={`question-${index}`}
                        type="h3"
                        component="h3"
                        className="text-legacy-dark"
                        htmlFor={`collapse-button-${index}`}
                      >
                        {faq.question}
                      </Typography>
                    }
                  >
                    {faq.answer}
                  </ExpansionPanel>
                </li>
              ),
          )}
        </ul>
      </div>
    );
  };

  return (
    <>
      <div className="min-[1280px]:pl-0 mx-24">
        <Typography
          className="!text-[24px] mb-48 font-medium text-legacy-textDark"
          withTab
          id="help-heading"
          type="h1"
          component="h1"
        >
          Frequently Asked Questions
        </Typography>

        <Typography type="caption4" component="p">
          We're happy to help.
        </Typography>
      </div>

      <div className="mx-24">
        {Object.keys(policy).length === 0 && !isAdmin ? (
          <div>
            <Typography withTab type="h3" component="h3">
              You have no in force policies with Slide Insurance at this time.
              Please contact the Client Experience Center for assistance:
              <div>
                <a href="tel:800-748-2030" className="phone" tabIndex="0">
                  (800) 748-2030
                </a>
              </div>
            </Typography>
          </div>
        ) : (
          <>
            {renderList(dataPolicies, 'Policies')}
            {renderList(dataClaims, 'Claims')}
            {renderList(dataPayments, 'Payments')}
          </>
        )}
      </div>
    </>
  );
};

export default Help;
